import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { MoneyPipe } from '@app/shared/pipes/money.pipe';
import { CampanhaService } from '@app/shared/components/api/services/campanha.service';
import { ApoioService } from '@app/shared/components/api/services/apoio.service';
import { LogsService } from '@app/shared/components/api/services/logs.service';
import { ConfiguracoesService } from '@app/shared/components/api/services/configuracoes.service';
import { DashboardService } from '@app/shared/components/api/services/dashboard.service';
import {
  DashboardCampanhaEstadoDto,
  DashboardApoioEstadoDto,
  DashboardUtilizadorDto,
  DashboardConfiguracaoGeralDto,
  DashboardGestaoPaginaDto,
} from '@app/shared/components/api/models/dashboard.model';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-estatisticas',
  imports: [
    CommonModule,
    RouterLink,
    TableModule,
    Tag,
    ButtonModule,
    Skeleton,
    MoneyPipe,
  ],
  providers: [
    CampanhaService,
    ApoioService,
    LogsService,
    ConfiguracoesService,
    DashboardService,
  ],
  templateUrl: './estatisticas.component.html',
  styleUrl: './estatisticas.component.scss',
  standalone: true,
})
export class EstatisticasComponent implements OnInit {
  private readonly campanhaService = inject(CampanhaService);
  private readonly apoioService = inject(ApoioService);
  private readonly logsService = inject(LogsService);
  private readonly configuracoesService = inject(ConfiguracoesService);
  private readonly dashboardService = inject(DashboardService);

  loading: boolean = true;

  // KPI Counts
  totalCampanhas: number = 0;
  totalUtilizadores: number = 0;
  totalPagamentos: number = 0;
  totalRegistos: number = 0;

  // Detailed Stats Breakdown
  campanhasPorEstado: DashboardCampanhaEstadoDto[] = [];
  apoiosPorEstado: DashboardApoioEstadoDto[] = [];
  utilizadoresStats: DashboardUtilizadorDto | null = null;
  configuracoesPorTipoGeral: DashboardConfiguracaoGeralDto[] = [];
  gestaoPaginaStats: DashboardGestaoPaginaDto | null = null;

  // Financial Indicators
  totalApoios: number = 0;
  totalTaxasServico: number = 0;
  totalLiquidoCampanhas: number = 0;
  taxaServicoConfig: number = 3.2;

  // Recent Data
  recentPagamentos: any[] = [];
  recentCampanhas: any[] = [];
  recentLogs: any[] = [];

  ngOnInit(): void {
    this.carregarDadosDashboard();
  }

  carregarDadosDashboard(): void {
    this.loading = true;

    forkJoin({
      dashboardCompleto: this.dashboardService.getDashboardCompleto(),
      campanhas: this.campanhaService.list({
        PageSize: 5,
        PageNumber: 1,
        OrderBy: 'id desc',
      }),
      apoios: this.apoioService.list({
        PageSize: 10,
        PageNumber: 1,
        OrderBy: 'id desc',
      }),
      logs: this.logsService.list({
        PageSize: 5,
        PageNumber: 1,
        OrderBy: 'id desc',
      }),
      taxa: this.configuracoesService.getTaxaServico(1),
      totaisApoiosConfirmados: this.dashboardService.getTotalApoiosConfirmados().pipe(
        catchError(() => of({ totalValor: 0, totalApoios: 0 }))
      ),
    }).subscribe({
      next: (res) => {
        const dashboard = res.dashboardCompleto?.data;

        // Totals from Dashboard Completo
        if (dashboard?.resumoGeral) {
          this.totalCampanhas = dashboard.resumoGeral.totalCampanhas ?? 0;
          this.totalPagamentos = dashboard.resumoGeral.totalApoios ?? 0;
          this.totalUtilizadores = dashboard.resumoGeral.totalUtilizador ?? 0;
          this.totalRegistos = dashboard.resumoGeral.totalRegistos ?? 0;
        } else {
          this.totalCampanhas = res.campanhas?.totalRecords || 0;
          this.totalPagamentos = res.apoios?.totalRecords || 0;
          this.totalRegistos = res.logs?.totalRecords || 0;
        }

        // Detailed Breakdowns
        this.campanhasPorEstado = dashboard?.campanhasPorEstado || [];
        this.apoiosPorEstado = dashboard?.apoiosPorEstado || [];
        this.utilizadoresStats = dashboard?.utilizadores || null;
        this.configuracoesPorTipoGeral =
          dashboard?.configuracoesPorTipoGeral || [];
        this.gestaoPaginaStats = dashboard?.gestaoPagina || null;

        // Recent items for tables
        this.recentCampanhas = res.campanhas?.data || [];
        this.recentPagamentos = res.apoios?.data || [];
        this.recentLogs = res.logs?.data || [];

        if (res.taxa !== undefined && res.taxa !== null) {
          this.taxaServicoConfig = Number(res.taxa) || 3.2;
        }

        // Calculate financial indicators from confirmed payments
        const totaisConfirmados = res.totaisApoiosConfirmados as { totalValor?: number; totalApoios?: number } | undefined;
        if (totaisConfirmados && (totaisConfirmados.totalValor || 0) > 0) {
          const totalBruto = totaisConfirmados.totalValor || 0;
          const totalTaxas = (totalBruto * this.taxaServicoConfig) / 100;
          this.totalApoios = totalBruto;
          this.totalTaxasServico = totalTaxas;
          this.totalLiquidoCampanhas = totalBruto - totalTaxas;
        } else {
          this.calcularTotaisFinanceiros(this.recentPagamentos);
        }

        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar dados do dashboard:', err);
        this.loading = false;
      },
    });
  }

  private calcularTotaisFinanceiros(pagamentos: any[]): void {
    let totalBruto = 0;
    let totalTaxas = 0;
    let totalLiquido = 0;

    for (const item of pagamentos) {
      const doadoTotal = item?.valorDoadoTotal ?? item?.valorApoio ?? 0;
      const taxa =
        item?.valorTaxa ??
        (item?.taxaServico
          ? (doadoTotal * item.taxaServico) / 100
          : (doadoTotal * this.taxaServicoConfig) / 100);
      const doadoCampanha = item?.valorDoadoCampanha ?? doadoTotal - taxa;

      totalBruto += Number(doadoTotal) || 0;
      totalTaxas += Number(taxa) || 0;
      totalLiquido += Number(doadoCampanha) || 0;
    }

    this.totalApoios = totalBruto;
    this.totalTaxasServico = totalTaxas;
    this.totalLiquidoCampanhas = totalLiquido;
  }

  getSeverity(
    state: string,
  ):
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | null
    | undefined {
    switch (state?.toLowerCase()) {
      case 'confirmado':
      case 'aprovado':
      case 'ativo':
      case 'activa':
      case 'em curso':
      case 'publicado':
        return 'success';
      case 'pendente':
      case 'em análise':
      case 'rascunho':
        return 'warn';
      case 'cancelado':
      case 'recusado':
      case 'suspensa':
        return 'danger';
      case 'retornado':
      case 'concluído':
      case 'concluida':
      case 'sucesso':
        return 'info';
      default:
        return 'secondary';
    }
  }
}
